import React, { useMemo, useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { applyFilterCategories, unselectFilterCategoryOptions } from '../../../../../../actions/searchActions';
import { NewTagIcon } from '../../../../../../assets/icons/NewTag.icon';
import styles from './styles.module.css';

function CheckBoxFilterBase({
  filterCategory,
  filterProvider,
  unselectFilterCategoryOptions,
  applyFilterCategories,
  source = 'category',
  matchIds = [],
  providerSlugs = [],
  title,
  ariaLabel,
  showDivider = true,
  useStagingMode = false,
  onStageChange = null,
  priorityProviderSlugs = [],

}) {
  const [stagedSelections, setStagedSelections] = useState({});
  const categoryIndex = useMemo(() => {
    if (source !== 'category') return -1;
    if (!Array.isArray(filterCategory)) return -1;
    const toKey = (v) => (v == null ? '' : String(v));
    const idsArr = Array.isArray(matchIds) ? matchIds : [matchIds];
    const idsSet = new Set(idsArr.map(toKey));
    if (idsSet.size === 0) return -1;
    const idOf = (cat) => {
      if (!cat) return undefined;
      if (cat.ID !== undefined) return cat.ID;
      if (cat.Id !== undefined) return cat.Id;
      return cat.id;
    };
    return filterCategory.findIndex((cat) => idsSet.has(toKey(idOf(cat))));
  }, [source, filterCategory, matchIds]);

  const categoryOptions = useMemo(() => {
    if (source !== 'category') return [];
    if (categoryIndex < 0) return [];
    const cat = filterCategory[categoryIndex];
    if (!cat || !Array.isArray(cat.Categories)) return [];
    return cat.Categories.map((opt, optionIndex) => {
      const key = `category-${categoryIndex}-${optionIndex}`;
      const reduxSelected = Boolean(opt && opt.selected);
      const selected = useStagingMode && stagedSelections[key] !== undefined
        ? stagedSelections[key]
        : reduxSelected;

      return {
        optionIndex,
        label: (opt && (opt.Name || opt.Value)) || '',
        selected,
        isNew: Boolean(opt && opt.is_new),
        type: 'category',
        categoryIndex,
      };
    });
  }, [source, filterCategory, categoryIndex, useStagingMode, stagedSelections]);

  const providersOptions = useMemo(() => {
    if (source !== 'provider') return [];
    const list = Array.isArray(filterProvider) ? filterProvider : [];
    const slugSet = new Set((Array.isArray(providerSlugs) ? providerSlugs : [providerSlugs]).filter(Boolean));
    const filtered = slugSet.size > 0 ? list.filter((p) => slugSet.has(p.slug)) : list;

    const mapped = filtered.map((p, idx) => {
      const key = `provider-${idx}`;
      const reduxSelected = Boolean(p.selected);
      const selected = useStagingMode && stagedSelections[key] !== undefined
        ? stagedSelections[key]
        : reduxSelected;

      const priorityIndex = priorityProviderSlugs.indexOf(p.slug);
      const priority = priorityIndex >= 0 ? priorityIndex : priorityProviderSlugs.length + idx;

      return {
        optionIndex: idx,
        label: p.name || p.slug || '',
        slug: p.slug || '',
        selected,
        isNew: Boolean(p.is_new),
        type: 'provider',
        priority,
      };
    });

    return mapped.sort((a, b) => a.priority - b.priority);
  }, [source, filterProvider, providerSlugs, useStagingMode, stagedSelections, priorityProviderSlugs]);

  const sortedOptions = source === 'provider' ? providersOptions : categoryOptions;

  const onToggle = useCallback(
    (option) => {
      if (!option) return;

      if (useStagingMode) {
        const key = source === 'provider'
          ? `provider-${option.optionIndex}`
          : `category-${option.categoryIndex}-${option.optionIndex}`;

        setStagedSelections(prev => {
          const newState = {
            ...prev,
            [key]: !option.selected
          };

          if (onStageChange) {
            onStageChange({
              source,
              categoryIndex: option.categoryIndex,
              optionIndex: option.optionIndex,
              newState
            });
          }

          return newState;
        });
      } else {
        if (source === 'provider') {
          unselectFilterCategoryOptions('provider', option.optionIndex, 0);
          applyFilterCategories();
          return;
        }
        if (source === 'category') {
          if (option.categoryIndex < 0) return;
          unselectFilterCategoryOptions('category', option.categoryIndex, option.optionIndex);
          applyFilterCategories();
        }
      }
    },
    [source, unselectFilterCategoryOptions, applyFilterCategories, useStagingMode, onStageChange]
  );

  if (sortedOptions.length === 0) return null;


  return (
    <div className={styles.container}>
      {title ? (
        <div className={styles.header}>
          <span className={styles.title}>{title}</span>
        </div>
      ) : null}

      <ul className={styles.checkboxList} role="group" aria-label={ariaLabel || title}>
        {sortedOptions.map((opt) => (
          <li key={`${source}-${opt.optionIndex}`} className={styles.checkboxItem}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                className={styles.checkboxInput}
                checked={opt.selected}
                onChange={() => onToggle(opt)}
                aria-checked={opt.selected}
                aria-label={opt.label}
              />
              <div className={styles.checkboxText}>
                <div className={styles.checkboxTextFlex}>
                  {source === 'provider' && (
                    <img src={`/assets/images/seguradoras/small/${opt.slug}.svg`} alt={opt.label || ''} />
                  )}
                  <span className={styles.checkboxLabelText}>{opt.label}</span>
                  {opt.isNew && <NewTagIcon />}
                </div>
              </div>
            </label>
          </li>
        ))}
      </ul>

      {showDivider ? <div className={styles.divider} /> : null}
    </div>
  );
}

const mapStateToProps = (state) => ({
  filterCategory: state.search.filterCategory,
  filterProvider: state.search.filterProvider,
});

export default connect(mapStateToProps, { unselectFilterCategoryOptions, applyFilterCategories })(CheckBoxFilterBase);



// WEBPACK FOOTER //
// ./src/views/v2/search/components/filter/components/CheckBoxFilter.jsx